import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/layout/Header';
import MobileNav from '@/components/layout/MobileNav';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Trees, UserPlus, Link2, Loader2, Pencil, Heart, Users, Crown, X, Sparkles,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const CURRENT_YEAR = new Date().getFullYear();

const getInitials = (name) =>
  (name || '?').split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase();

/**
 * Compute a generation bucket for each node.
 *   • Gen 0 = oldest living generation (anyone with no parent edges pointing AT them)
 *   • Child of a gen-N node lives at gen N+1
 *   • Spouses are coerced to the same generation as their partner
 *   • If graph has cycles (shouldn't, but just in case), we cap the depth at 6 and fall back to birth-year buckets
 */
function computeGenerations(nodes, relations) {
  const idToNode = new Map(nodes.map((n) => [n.id, n]));
  const parents = new Map(); // child -> Set of parent ids
  const children = new Map(); // parent -> Set of child ids
  const spouses = new Map(); // id -> Set of spouse ids
  for (const r of relations) {
    if (r.type === 'parent') {
      // from = parent, to = child
      if (!parents.has(r.to_id)) parents.set(r.to_id, new Set());
      parents.get(r.to_id).add(r.from_id);
      if (!children.has(r.from_id)) children.set(r.from_id, new Set());
      children.get(r.from_id).add(r.to_id);
    } else if (r.type === 'spouse') {
      if (!spouses.has(r.from_id)) spouses.set(r.from_id, new Set());
      if (!spouses.has(r.to_id)) spouses.set(r.to_id, new Set());
      spouses.get(r.from_id).add(r.to_id);
      spouses.get(r.to_id).add(r.from_id);
    }
  }

  const gen = new Map();
  // seed: nodes with no parents
  const roots = nodes.filter((n) => !parents.has(n.id)).map((n) => n.id);
  const queue = roots.map((id) => [id, 0]);
  while (queue.length) {
    const [id, g] = queue.shift();
    if (gen.has(id) && gen.get(id) <= g) continue;
    gen.set(id, g);
    for (const child of children.get(id) || []) {
      queue.push([child, g + 1]);
    }
  }
  // spouse alignment: pull the higher-numbered spouse down to match partner (iterate to fixpoint)
  let changed = true;
  let safety = 10;
  while (changed && safety-- > 0) {
    changed = false;
    for (const [id, partners] of spouses.entries()) {
      for (const p of partners) {
        const a = gen.get(id);
        const b = gen.get(p);
        if (a != null && b != null && a !== b) {
          const target = Math.min(a, b);
          if (gen.get(id) !== target) { gen.set(id, target); changed = true; }
          if (gen.get(p) !== target) { gen.set(p, target); changed = true; }
        } else if (a != null && b == null) {
          gen.set(p, a); changed = true;
        } else if (b != null && a == null) {
          gen.set(id, b); changed = true;
        }
      }
    }
  }
  // Anyone still ungrouped: bucket by birth_year
  for (const n of nodes) {
    if (!gen.has(n.id)) {
      if (n.birth_year) {
        const age = CURRENT_YEAR - n.birth_year;
        // 0: 70+, 1: 45-69, 2: 18-44, 3: <18
        gen.set(n.id, age >= 70 ? 0 : age >= 45 ? 1 : age >= 18 ? 2 : 3);
      } else {
        gen.set(n.id, 1);
      }
    }
  }
  return { gen, spouses, parents, children, idToNode };
}

const GENERATION_LABELS = [
  'Elders',
  'Grandparents',
  'Parents',
  'Our generation',
  'Children',
  'Grandchildren',
  'Great-grandchildren',
];

function NodeCard({ node, onEdit, onBlock }) {
  return (
    <button
      onClick={() => onEdit(node)}
      className={`group text-left rounded-2xl border p-3 w-[170px] transition-all hover:-translate-y-0.5 hover:shadow-md relative ${
        node.deceased
          ? 'bg-stone-50 border-stone-200 opacity-80'
          : node.is_self
          ? 'bg-gradient-to-br from-teal-50 to-white border-teal-300 ring-2 ring-teal-200'
          : 'bg-white border-stone-200 hover:border-teal-300'
      }`}
      data-testid={`tree-node-${node.id}`}
    >
      {node.deceased && (
        <span className="absolute top-2 right-2 text-[10px] text-stone-500 bg-white border border-stone-200 rounded-full px-1.5 py-0.5">
          In memory
        </span>
      )}
      {node.is_self && !node.deceased && (
        <span className="absolute top-2 right-2 text-[10px] text-teal-700 bg-teal-100 rounded-full px-1.5 py-0.5">
          You
        </span>
      )}
      <div className="flex items-center gap-3">
        <Avatar className={`h-11 w-11 ${node.deceased ? 'grayscale' : ''}`}>
          <AvatarImage src={node.avatar} />
          <AvatarFallback className="bg-teal-600 text-white text-sm">{getInitials(node.name)}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-stone-900 truncate text-sm">{node.name}</p>
          {node.family_role && (
            <p className="text-xs text-teal-600 font-medium truncate">{node.family_role}</p>
          )}
          {node.birth_year && (
            <p className="text-[11px] text-stone-500">
              {node.deceased ? '†' : 'b.'} {node.birth_year}
            </p>
          )}
        </div>
      </div>
      {node.kind === 'placeholder' && (
        <div className="mt-2 text-[10px] text-stone-400 inline-flex items-center gap-1">
          <Sparkles className="w-2.5 h-2.5" /> Added by family
        </div>
      )}
    </button>
  );
}

export default function TreePage() {
  const { user } = useAuth();
  const [tree, setTree] = useState({ nodes: [], relations: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // node being edited
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editBirthYear, setEditBirthYear] = useState('');
  const [editDeceased, setEditDeceased] = useState(false);
  const [saving, setSaving] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [addRole, setAddRole] = useState('');
  const [addBirthYear, setAddBirthYear] = useState('');
  const [addDeceased, setAddDeceased] = useState(false);

  const [relOpen, setRelOpen] = useState(false);
  const [relFrom, setRelFrom] = useState('');
  const [relTo, setRelTo] = useState('');
  const [relType, setRelType] = useState('parent');

  const fetchTree = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tree`, { withCredentials: true });
      setTree(data);
    } catch (err) {
      toast.error('Could not load family tree');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTree(); }, [fetchTree]);

  const { gen, spouses, children, idToNode } = useMemo(
    () => computeGenerations(tree.nodes, tree.relations),
    [tree]
  );

  // Group nodes by generation
  const byGen = useMemo(() => {
    const g = new Map();
    for (const n of tree.nodes) {
      const k = gen.get(n.id) ?? 1;
      if (!g.has(k)) g.set(k, []);
      g.get(k).push(n);
    }
    // Sort within each gen: self first, then by birth year ascending, then name
    for (const [, list] of g.entries()) {
      list.sort((a, b) => {
        if (a.is_self) return -1;
        if (b.is_self) return 1;
        const ay = a.birth_year || 9999;
        const by = b.birth_year || 9999;
        if (ay !== by) return ay - by;
        return (a.name || '').localeCompare(b.name || '');
      });
    }
    return [...g.entries()].sort((a, b) => a[0] - b[0]);
  }, [tree, gen]);

  const openEdit = (node) => {
    setEditing(node);
    setEditName(node.name || '');
    setEditRole(node.family_role || '');
    setEditBirthYear(node.birth_year ? String(node.birth_year) : '');
    setEditDeceased(!!node.deceased);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        family_role: editRole.trim(),
        birth_year: editBirthYear ? parseInt(editBirthYear, 10) : null,
      };
      if (editing.kind === 'member') {
        await axios.patch(`${API}/users/${editing.id}/role`, payload, { withCredentials: true });
      } else {
        await axios.patch(`${API}/tree/placeholders/${editing.id}`, {
          name: editName.trim(),
          ...payload,
          deceased: editDeceased,
          avatar_url: editing.avatar,
        }, { withCredentials: true });
      }
      toast.success('Saved');
      setEditing(null);
      await fetchTree();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deletePlaceholder = async () => {
    if (!editing || editing.kind !== 'placeholder') return;
    if (!window.confirm(`Remove ${editing.name} from the tree?`)) return;
    try {
      await axios.delete(`${API}/tree/placeholders/${editing.id}`, { withCredentials: true });
      toast.success('Removed');
      setEditing(null);
      await fetchTree();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const addPlaceholder = async () => {
    if (!addName.trim()) return toast.error('Name required');
    setSaving(true);
    try {
      await axios.post(`${API}/tree/placeholders`, {
        name: addName.trim(),
        family_role: addRole.trim(),
        birth_year: addBirthYear ? parseInt(addBirthYear, 10) : null,
        deceased: addDeceased,
      }, { withCredentials: true });
      toast.success(`${addName} added`);
      setAddOpen(false);
      setAddName(''); setAddRole(''); setAddBirthYear(''); setAddDeceased(false);
      await fetchTree();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Add failed');
    } finally {
      setSaving(false);
    }
  };

  const addRelation = async () => {
    if (!relFrom || !relTo) return toast.error('Select both people');
    if (relFrom === relTo) return toast.error("Can't link someone to themselves");
    setSaving(true);
    try {
      await axios.post(`${API}/tree/relations`, {
        from_id: relFrom, to_id: relTo, type: relType,
      }, { withCredentials: true });
      toast.success('Relationship added');
      setRelOpen(false);
      setRelFrom(''); setRelTo(''); setRelType('parent');
      await fetchTree();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteRelation = async (relId) => {
    if (!window.confirm('Remove this relationship?')) return;
    try {
      await axios.delete(`${API}/tree/relations/${relId}`, { withCredentials: true });
      await fetchTree();
    } catch (err) {
      toast.error('Remove failed');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-20 md:pb-8" data-testid="tree-page">
      <Header />
      <main className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-teal-50 flex items-center justify-center">
              <Trees className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-stone-900">Family tree</h1>
              <p className="text-sm text-stone-500">
                {tree.nodes.length} {tree.nodes.length === 1 ? 'person' : 'people'} · {tree.relations.length} connection{tree.relations.length === 1 ? '' : 's'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setRelOpen(true)}
              className="rounded-full border-stone-200"
              disabled={tree.nodes.length < 2}
              data-testid="add-relation-btn"
            >
              <Link2 className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Link people</span>
            </Button>
            <Button
              onClick={() => setAddOpen(true)}
              className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
              data-testid="add-person-btn"
            >
              <UserPlus className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Add relative</span>
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
          </div>
        ) : tree.nodes.length === 0 ? (
          <div className="card-soft text-center py-16" data-testid="tree-empty">
            <Trees className="w-14 h-14 text-teal-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-stone-900 mb-2">Start your family tree</h2>
            <p className="text-stone-500 max-w-md mx-auto mb-6">
              Add your parents, grandparents, aunts, uncles — even the ones who aren't on Kinsy yet. You can always invite them later.
            </p>
            <Button onClick={() => setAddOpen(true)} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full">
              <UserPlus className="w-4 h-4 mr-2" /> Add the first relative
            </Button>
          </div>
        ) : (
          <div className="space-y-8" data-testid="tree-body">
            {byGen.map(([g, list], idx) => {
              const isLast = idx === byGen.length - 1;
              const totalGens = byGen.length;
              const label = totalGens === 1
                ? 'Everyone'
                : (GENERATION_LABELS[Math.min(g, GENERATION_LABELS.length - 1)] || `Generation ${g + 1}`);
              return (
                <div key={g} className="relative">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs uppercase tracking-wide font-semibold text-teal-700 bg-teal-50 rounded-full px-3 py-1 inline-flex items-center gap-1">
                      {g === 0 && <Crown className="w-3 h-3" />}
                      {label}
                    </span>
                    <span className="text-xs text-stone-400">{list.length} {list.length === 1 ? 'person' : 'people'}</span>
                  </div>
                  <div className="flex flex-wrap gap-3 md:gap-5">
                    {list.map((node) => {
                      const partners = [...(spouses.get(node.id) || [])]
                        .map((pid) => idToNode.get(pid))
                        .filter(Boolean);
                      return (
                        <div key={node.id} className="flex items-center gap-3">
                          <NodeCard node={node} onEdit={openEdit} />
                          {partners.length > 0 && partners.some((p) => (gen.get(p.id) ?? 1) === g) && (
                            <Heart className="w-4 h-4 text-pink-400 shrink-0" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {!isLast && (
                    <div className="flex justify-center mt-4">
                      <div className="w-px h-6 bg-gradient-to-b from-stone-200 to-teal-200" />
                    </div>
                  )}
                </div>
              );
            })}

            {/* Relationships list */}
            {tree.relations.length > 0 && (
              <div className="card-soft mt-8" data-testid="relations-list">
                <h3 className="text-sm font-semibold text-stone-700 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-teal-600" /> Relationships
                </h3>
                <div className="flex flex-wrap gap-2">
                  {tree.relations.map((r) => {
                    const a = idToNode.get(r.from_id);
                    const b = idToNode.get(r.to_id);
                    if (!a || !b) return null;
                    const label = r.type === 'parent' ? `${a.name} → parent of → ${b.name}` :
                                  r.type === 'spouse' ? `${a.name} ♥ ${b.name}` :
                                  `${a.name} ⇔ ${b.name}`;
                    return (
                      <span
                        key={r.id}
                        className="inline-flex items-center gap-2 text-xs bg-stone-50 border border-stone-200 rounded-full pl-3 pr-1 py-1 text-stone-700"
                        data-testid={`relation-${r.id}`}
                      >
                        {label}
                        <button
                          onClick={() => deleteRelation(r.id)}
                          className="w-5 h-5 rounded-full hover:bg-red-100 text-stone-400 hover:text-red-600 flex items-center justify-center"
                          title="Remove"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Edit node dialog */}
        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-md rounded-3xl" data-testid="edit-node-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="w-4 h-4 text-teal-600" />
                {editing?.kind === 'member' ? 'Edit family details' : 'Edit relative'}
              </DialogTitle>
              <DialogDescription>
                {editing?.kind === 'member'
                  ? "Set their family role and birth year to place them on the tree."
                  : 'This person isn\'t on Kinsy yet — edit their placeholder details.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {editing?.kind === 'placeholder' && (
                <div className="space-y-1.5">
                  <Label>Name</Label>
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} data-testid="edit-name" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Family role</Label>
                <Input
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value)}
                  placeholder="e.g., Mom, Grandpa, Uncle Joe"
                  maxLength={40}
                  data-testid="edit-role"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Birth year (optional, helps ordering)</Label>
                <Input
                  value={editBirthYear}
                  onChange={(e) => setEditBirthYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="e.g., 1952"
                  inputMode="numeric"
                  data-testid="edit-birth-year"
                />
              </div>
              {editing?.kind === 'placeholder' && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="deceased"
                    checked={editDeceased}
                    onCheckedChange={setEditDeceased}
                    data-testid="edit-deceased"
                  />
                  <Label htmlFor="deceased" className="cursor-pointer text-stone-600">
                    In loving memory (deceased)
                  </Label>
                </div>
              )}
            </div>
            <DialogFooter className="mt-2">
              {editing?.kind === 'placeholder' && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={deletePlaceholder}
                  className="mr-auto rounded-full border-red-200 text-red-600 hover:bg-red-50"
                  data-testid="delete-placeholder-btn"
                >
                  Remove
                </Button>
              )}
              <Button variant="outline" onClick={() => setEditing(null)} className="rounded-full">Cancel</Button>
              <Button onClick={saveEdit} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="save-node-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add placeholder dialog */}
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent className="max-w-md rounded-3xl" data-testid="add-person-dialog">
            <DialogHeader>
              <DialogTitle>Add a relative</DialogTitle>
              <DialogDescription>
                Not on Kinsy yet? Add them as a placeholder so your tree stays complete.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="e.g., Grandma Rose" data-testid="add-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Family role</Label>
                <Input value={addRole} onChange={(e) => setAddRole(e.target.value)} placeholder="e.g., Grandma" maxLength={40} data-testid="add-role-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Birth year (optional)</Label>
                <Input
                  value={addBirthYear}
                  onChange={(e) => setAddBirthYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="e.g., 1945"
                  inputMode="numeric"
                  data-testid="add-birth-year-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="add-deceased" checked={addDeceased} onCheckedChange={setAddDeceased} />
                <Label htmlFor="add-deceased" className="cursor-pointer text-stone-600">
                  In loving memory (deceased)
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)} className="rounded-full">Cancel</Button>
              <Button onClick={addPlaceholder} disabled={saving || !addName.trim()} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="submit-add-person-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                Add to tree
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add relation dialog */}
        <Dialog open={relOpen} onOpenChange={setRelOpen}>
          <DialogContent className="max-w-md rounded-3xl" data-testid="relation-dialog">
            <DialogHeader>
              <DialogTitle>Link two people</DialogTitle>
              <DialogDescription>
                Choose the relationship. For "parent", the first person is the parent of the second.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Relationship type</Label>
                <Select value={relType} onValueChange={setRelType}>
                  <SelectTrigger data-testid="relation-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parent">Parent of</SelectItem>
                    <SelectItem value="spouse">Spouse / partner</SelectItem>
                    <SelectItem value="sibling">Sibling</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>{relType === 'parent' ? 'Parent' : 'Person A'}</Label>
                  <Select value={relFrom} onValueChange={setRelFrom}>
                    <SelectTrigger data-testid="relation-from"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {tree.nodes.map((n) => (
                        <SelectItem key={n.id} value={n.id}>{n.name}{n.family_role ? ` (${n.family_role})` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{relType === 'parent' ? 'Child' : 'Person B'}</Label>
                  <Select value={relTo} onValueChange={setRelTo}>
                    <SelectTrigger data-testid="relation-to"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {tree.nodes.map((n) => (
                        <SelectItem key={n.id} value={n.id}>{n.name}{n.family_role ? ` (${n.family_role})` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRelOpen(false)} className="rounded-full">Cancel</Button>
              <Button
                onClick={addRelation}
                disabled={saving || !relFrom || !relTo}
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
                data-testid="submit-relation-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
                Link
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
      <MobileNav />
    </div>
  );
}
